import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { INBOX } from "@/constants/testIds";
import { Archive, Mail, Megaphone, BellOff, BellRing } from "lucide-react";
import eventStream from "@/lib/eventStream";

const FOLDERS = [
  { key: "contact", label: "Contact", icon: Mail, kinds: ["contact"] },
  { key: "advertise", label: "Advertise", icon: Megaphone, kinds: ["advertise"] },
  { key: "newsletter", label: "Newsletter Signups", icon: BellRing, kinds: ["newsletter_email", "newsletter_sms"] },
  { key: "unsubscribe", label: "Unsubscribes", icon: BellOff, kinds: ["unsubscribe_email", "unsubscribe_sms"] },
];

export default function FormInbox() {
  const [folder, setFolder] = useState(FOLDERS[0]);
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [counts, setCounts] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  // Track folder-specific totals from the live pulse; refetch on change.
  const lastFolderTotalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [c, ...lists] = await Promise.all([
        api.get("/admin/forms/counts"),
        ...folder.kinds.map((k) => api.get(`/admin/forms?kind=${k}&limit=50`)),
      ]);
      if (cancelled) return;
      setCounts(c.data);
      const all = lists.flatMap((r) => r.data.items);
      all.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setItems(all);
      setSel((prev) => {
        if (all.length === 0) return null;
        if (prev && all.find((x) => x.id === prev.id)) return prev;
        return all[0];
      });
    })();
    return () => { cancelled = true; };
  }, [folder, refreshKey]);

  // Live updates via the SSE bus — refresh counts immediately and the active
  // folder list only when its specific totals change.
  useEffect(() => {
    lastFolderTotalRef.current = null;
    const unsub = eventStream.subscribe("inbox_counts", (snap) => {
      if (snap?.by_kind) setCounts(snap.by_kind);
      const folderTotal = folder.kinds.reduce(
        (acc, k) => acc + ((snap?.by_kind?.[k]?.total) || 0),
        0,
      );
      if (lastFolderTotalRef.current === null) {
        lastFolderTotalRef.current = folderTotal;
        return;
      }
      if (folderTotal !== lastFolderTotalRef.current) {
        lastFolderTotalRef.current = folderTotal;
        setRefreshKey((k) => k + 1);
      }
    });
    return unsub;
  }, [folder]);

  const reload = () => setRefreshKey((k) => k + 1);

  async function markRead(id) { await api.post(`/admin/forms/${id}/read`); reload(); }
  async function archive(id) { await api.post(`/admin/forms/${id}/archive`); reload(); }

  function unreadCount(f) {
    return f.kinds.reduce((acc, k) => acc + (counts[k]?.unread || 0), 0);
  }
  function totalCount(f) {
    return f.kinds.reduce((acc, k) => acc + (counts[k]?.total || 0), 0);
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      <div className="w-56 border-r border-zinc-800 py-2">
        {FOLDERS.map((f) => (
          <button
            key={f.key}
            data-testid={INBOX.folder(f.key)}
            onClick={() => setFolder(f)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
              folder.key === f.key ? "border-white text-white bg-zinc-900/40" : "border-transparent text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <f.icon size={15} strokeWidth={1.75} />
            <span className="flex-1 text-left">{f.label}</span>
            <span className="mono text-[11px] text-zinc-500">{totalCount(f)}</span>
            {unreadCount(f) > 0 && <span className="dot bg-emerald-500" />}
          </button>
        ))}
      </div>

      <div className="w-[420px] border-r border-zinc-800 flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-800 label-tiny">{folder.label}</div>
        <div className="flex-1 overflow-auto divide-y divide-zinc-800">
          {items.length === 0 && <div className="px-4 py-8 text-sm text-zinc-500 text-center">Empty.</div>}
          {items.map(s => (
            <button
              key={s.id}
              data-testid={INBOX.item(s.id)}
              onClick={() => setSel(s)}
              className={`w-full text-left px-4 py-3 hover:bg-zinc-900/40 transition-colors border-l-2 ${
                sel?.id === s.id ? "border-white bg-zinc-900/60" : "border-transparent"
              } ${!s.is_read ? "" : "opacity-70"}`}
            >
              <div className="flex items-center gap-2">
                {!s.is_read && <span className="dot bg-emerald-500" />}
                <div className="text-sm text-zinc-100 truncate flex-1">{s.name || s.email || s.phone || "Anonymous"}</div>
                <div className="text-[11px] mono text-zinc-500">{(s.created_at || "").slice(5, 16).replace("T", " ")}</div>
              </div>
              {s.message && <div className="mt-1 text-[11px] text-zinc-500 line-clamp-1">{s.message}</div>}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {!sel ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Select a submission.</div>
        ) : (
          <div className="max-w-3xl">
            <div className="label-tiny">{sel.kind}</div>
            <h2 className="font-display text-2xl font-black mt-1">{sel.name || sel.email || sel.phone || "Anonymous"}</h2>
            <div className="mt-1 text-xs mono text-zinc-500">{sel.created_at}</div>

            <dl className="grid grid-cols-2 gap-3 mt-5 text-sm">
              {sel.email && <Field label="Email" value={sel.email} />}
              {sel.phone && <Field label="Phone" value={sel.phone} />}
              {sel.company && <Field label="Company" value={sel.company} />}
            </dl>

            {sel.message && (
              <div className="mt-5 border border-zinc-800 rounded-sm p-4 bg-zinc-900/30 whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed">
                {sel.message}
              </div>
            )}

            <div className="mt-6 flex gap-2">
              {!sel.is_read && (
                <button
                  data-testid={INBOX.markRead}
                  onClick={() => markRead(sel.id)}
                  className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-white px-3 py-1.5 rounded-sm text-xs"
                >
                  Mark Read
                </button>
              )}
              <button
                data-testid={INBOX.archive}
                onClick={() => archive(sel.id)}
                className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-white px-3 py-1.5 rounded-sm text-xs"
              >
                <Archive size={13} /> Archive
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="border border-zinc-800 rounded-sm p-3">
      <div className="label-tiny">{label}</div>
      <div className="mono text-sm text-zinc-200 mt-1 break-all">{value}</div>
    </div>
  );
}
